import API from "serviceshift-ui/api-client";

import { reportApiFailure } from "@/vuex/interceptors";

// Each widget request opts out of the interceptor's generic flash (see
// widgetRequestConfig) so a single failure produces one banner naming the
// widget rather than that plus "An unexpected error occurred". The interceptor's
// Datadog report lives inside the branch that flag skips, so report here
// instead: suppressing the banner must not also suppress the telemetry, and
// reporting unconditionally is an improvement on the interceptor, which stayed
// silent whenever the server supplied a message of its own.
export const widgetErrorHandler = (label) => (error) => {
  reportApiFailure(error, "dashboard_widget");
  window.notify.error(`Unable to load ${label}`);
};

// A fresh config (and a fresh headers object) per call: the request interceptor
// writes Accept/Content-Type onto config.headers, so a shared literal would be
// mutated in place by five concurrent requests.
const widgetRequestConfig = (filters) => ({
  method: "GET",
  headers: { ignoreError: true },
  params: filters
});

export default {
  fetchDashboardData({ commit, dispatch }, filters) {
    commit("replace", {
      state: "dashboardLoading",
      data: true
    });
    Promise.all([
      dispatch("fetchOpportunitiesChart", filters).catch(
        widgetErrorHandler("Opportunities")
      ),
      dispatch("fetchRevenueChart", filters).catch(
        widgetErrorHandler("Revenue")
      ),
      dispatch("fetchMembershipsSoldChart", filters).catch(
        widgetErrorHandler("Memberships Sold")
      ),
      dispatch("fetchTechLeaderboard", filters).catch(
        widgetErrorHandler("Tech Leaderboard")
      ),
      dispatch("fetchPerformanceSummary", filters).catch(
        widgetErrorHandler("Performance Summary")
      )
    ])
      .then((values) => {
        commit("replace", {
          state: "dashboardData",
          data: {
            opportunities: (values[0] || {}).data,
            revenue: (values[1] || {}).data,
            memberships: (values[2] || {}).data,
            leaderboard: (values[3] || {}).data,
            summary: (values[4] || {}).data
          }
        });
        commit("replace", {
          state: "dashboardLoading",
          data: false
        });
      })
      .catch(() => {
        commit("replace", {
          state: "dashboardLoading",
          data: false
        });
      });
  },
  fetchOpportunitiesChart(_, filters) {
    return API.makeRequest(
      "/v2/metrics/series/opportunity_count",
      widgetRequestConfig(filters)
    );
  },
  fetchRevenueChart(_, filters) {
    return API.makeRequest(
      "/v2/metrics/series/job_revenue",
      widgetRequestConfig(filters)
    );
  },
  fetchMembershipsSoldChart(_, filters) {
    return API.makeRequest(
      "/v2/metrics/series/memberships_sold",
      widgetRequestConfig(filters)
    );
  },
  fetchTechLeaderboard(_, filters) {
    return API.makeRequest(
      "/v2/metrics/technician_leaderboard",
      widgetRequestConfig(filters)
    );
  },
  fetchPerformanceSummary(_, filters) {
    return API.makeRequest(
      "/v2/metrics/performance_summary",
      widgetRequestConfig(filters)
    );
  },
  setDashboardFilters({ commit, dispatch, state }, filters) {
    const previousFilters = { ...state.dashboardFilters };
    const filtersChanged =
      JSON.stringify(previousFilters) !== JSON.stringify(filters);
    if (filters.department_id) {
      filters.department_id = filters.department_id.join(",");
    }
    commit("update", {
      state: "dashboardFilters",
      data: filters
    });
    const { base_range, comparison_range, granularity } =
      state.dashboardFilters;
    if (filtersChanged && base_range && comparison_range && granularity) {
      dispatch("fetchDashboardData", state.dashboardFilters);
    }
  },
  clearDashboardData({ commit }) {
    commit("replace", {
      state: "dashboardData",
      data: {
        opportunities: null,
        revenue: null,
        memberships: null,
        leaderboard: null,
        summary: null
      }
    });
  }
};
